import React, { forwardRef, useImperativeHandle } from 'react';
import { Typography, Stack, Box } from '@mui/material';
import { Formik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';

import {
    UserAnswers,
    shouldShowHomeOfficeQuestion,
    shouldShowHomeOfficeDetails,
    hasNoReimbursements,
    getAnswersForValidation,
} from '../../types';
import WhyImportantLink from '../../components/WhyImportantLink';
import { ACCOUNTING_SETUP_HELP_CONTEXT } from '../../hooks/useOpenAccountingSetupHelp';

import YesNoField from 'modules/common/components/YesNoField';
import AmountField from 'modules/common/components/MUI/AmountField';
import { calculateHomeOfficePercentage } from 'modules/common/components/AccountablePlanV2/calculations';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';

// Validation schema
const validationSchema = Yup.object().shape({
    drivesForBusiness: Yup.string()
        .required('Please answer this question')
        .oneOf(['yes', 'no'], 'Please select Yes or No'),
    rentsOfficeSpace: Yup.string()
        .required('Please answer this question')
        .oneOf(['yes', 'no'], 'Please select Yes or No'),
    usesHomeAsOffice: Yup.string().when('rentsOfficeSpace', {
        is: 'no',
        then: (schema) =>
            schema.required('Please answer this question').oneOf(['yes', 'no'], 'Please select Yes or No'),
        otherwise: (schema) => schema.notRequired(),
    }),
    rentOrOwn: Yup.string().when('usesHomeAsOffice', {
        is: 'yes',
        then: (schema) =>
            schema.required('Please select rent or own').oneOf(['rent', 'own'], 'Please select Rent or Own'),
        otherwise: (schema) => schema.notRequired(),
    }),
    officeSqft: Yup.string().when('usesHomeAsOffice', {
        is: 'yes',
        then: (schema) =>
            schema
                .required('Office square footage is required')
                .test('is-number', 'Must be a valid number', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return !Number.isNaN(Number(value));
                })
                .test('not-negative', 'Cannot be negative', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return Number(value) >= 0;
                })
                .test('is-positive', 'Must be greater than 0', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return Number(value) > 0;
                })
                .test(
                    'not-greater-than-total',
                    'Office square footage cannot be greater than total home square footage',
                    function validateOfficeNotGreaterThanTotal(value) {
                        const totalSqft = this.parent.totalHomeSqft;
                        if (
                            totalSqft &&
                            value &&
                            Number(value) > 0 &&
                            Number(totalSqft) > 0 &&
                            Number(value) > Number(totalSqft)
                        ) {
                            return this.createError({
                                message: 'Office square footage cannot be greater than total home square footage',
                            });
                        }
                        return true;
                    }
                ),
        otherwise: (schema) => schema.notRequired(),
    }),
    totalHomeSqft: Yup.string().when('usesHomeAsOffice', {
        is: 'yes',
        then: (schema) =>
            schema
                .required('Total home square footage is required')
                .test('is-number', 'Must be a valid number', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return !Number.isNaN(Number(value));
                })
                .test('not-negative', 'Cannot be negative', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return Number(value) >= 0;
                })
                .test('is-positive', 'Must be greater than 0', (value) => {
                    if (!value) {
                        return true;
                    } // Let required handle empty values
                    return Number(value) > 0;
                })
                .test(
                    'not-less-than-office',
                    'Total home square footage must be greater than or equal to office square footage',
                    function validateTotalNotLessThanOffice(value) {
                        const { officeSqft } = this.parent;
                        if (
                            officeSqft &&
                            value &&
                            Number(value) > 0 &&
                            Number(officeSqft) > 0 &&
                            Number(value) < Number(officeSqft)
                        ) {
                            return this.createError({
                                message:
                                    'Total home square footage must be greater than or equal to office square footage',
                            });
                        }
                        return true;
                    }
                ),
        otherwise: (schema) => schema.notRequired(),
    }),
    percentage: Yup.string(),
});

interface ReimbursementQuestionnaireProps {
    initialValues: UserAnswers;
    homeAddress: string;
    onSubmit: (values: UserAnswers) => void;
    setCanProceed?: (canProceed: boolean) => void;
}

const defaultProps: Partial<ReimbursementQuestionnaireProps> = {
    setCanProceed: () => {},
};

export interface ReimbursementQuestionnaireRef {
    getCurrentValues: () => UserAnswers;
}

function QuestionnaireFormContent({
    values,
    errors,
    setFieldValue,
    homeAddress,
    setCanProceed = () => {},
    touched,
}: {
    values: UserAnswers;
    errors: Partial<Record<keyof UserAnswers, string>>;
    setFieldValue: (field: string, value: string) => void;
    homeAddress: string;
    setCanProceed?: (canProceed: boolean) => void;
    touched: Partial<Record<keyof UserAnswers, boolean>>;
}) {
    const { sendTrackingEvent } = useEventTracking();
    // Calculate percentage when office or home square footage changes (only if using home as office)
    React.useEffect(() => {
        if (values.usesHomeAsOffice === 'yes') {
            const officeSqft = parseFloat(values.officeSqft);
            const totalSqft = parseFloat(values.totalHomeSqft);

            if (officeSqft > 0 && totalSqft > 0) {
                const calculatedPercentage = calculateHomeOfficePercentage(officeSqft, totalSqft) * 100;
                const percentage = calculatedPercentage.toFixed(2);
                setFieldValue('percentage', percentage);
            }
        }
        // Don't reset percentage when not using home as office - let it stay as is
    }, [values.usesHomeAsOffice, values.officeSqft, values.totalHomeSqft, setFieldValue]);

    // Track individual question answers
    const handleQuestionChange = React.useCallback(
        (questionId: string, value: string) => {
            setFieldValue(questionId, value);

            // Track individual question answers
            sendTrackingEvent('Reimbursement Question Answered', {
                category: 'accounting_setup',
                action: 'question_answered',
                stepName: 'reimbursement_questionnaire',
                questionId,
                answer: value,
                // Include all current answers as properties
                drivesForBusiness: values.drivesForBusiness,
                rentsOfficeSpace: values.rentsOfficeSpace,
                usesHomeAsOffice: values.usesHomeAsOffice,
                rentOrOwn: values.rentOrOwn,
                officeSqft: values.officeSqft,
                totalHomeSqft: values.totalHomeSqft,
                percentage: values.percentage,
            });
        },
        [values, setFieldValue, sendTrackingEvent]
    );

    // Update canProceed when form validity changes
    React.useEffect(() => {
        if (setCanProceed) {
            // Use validation helper to clear home office values when office space is "yes"
            const validationAnswers = getAnswersForValidation(values);
            const isValidForValidation = Object.keys(validationAnswers).every((key) => {
                const value = validationAnswers[key as keyof UserAnswers];
                // Check if required fields are filled
                if (key === 'drivesForBusiness' || key === 'rentsOfficeSpace') {
                    return value === 'yes' || value === 'no';
                }
                if (key === 'usesHomeAsOffice' && shouldShowHomeOfficeQuestion(values)) {
                    return value === 'yes' || value === 'no';
                }
                if (key === 'rentOrOwn' && shouldShowHomeOfficeDetails(values)) {
                    return value === 'rent' || value === 'own';
                }
                if ((key === 'officeSqft' || key === 'totalHomeSqft') && shouldShowHomeOfficeDetails(values)) {
                    return value && !Number.isNaN(parseFloat(value)) && parseFloat(value) > 0;
                }
                return true;
            });
            setCanProceed(isValidForValidation);
        }
    }, [values, setCanProceed]);

    return (
        <Form>
            <Stack spacing={2} pb={5} sx={{ textAlign: 'left' }}>
                <Typography variant="h1" sx={{ lineHeight: 1.2, mt: 0, mb: 0 }}>
                    Find expenses you can reimburse
                </Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>
                    As an S Corp, your business can pay you back for certain business expenses you paid personally.
                    Answer a few questions to see what may qualify.
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Stack spacing={4} mt={3}>
                        <YesNoField
                            name="drivesForBusiness"
                            label="Did you drive for business purposes?"
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                            ]}
                            onChange={(value) => handleQuestionChange('drivesForBusiness', value as string)}
                        />

                        <YesNoField
                            name="rentsOfficeSpace"
                            label="Do you rent an office space?"
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                            ]}
                            onChange={(value) => handleQuestionChange('rentsOfficeSpace', value as string)}
                        />

                        {shouldShowHomeOfficeQuestion(values) && (
                            <YesNoField
                                name="usesHomeAsOffice"
                                label={`Do you use ${homeAddress} as a home office?`}
                                options={[
                                    { value: 'yes', label: 'Yes' },
                                    { value: 'no', label: 'No' },
                                ]}
                                onChange={(value) => handleQuestionChange('usesHomeAsOffice', value as string)}
                            />
                        )}
                    </Stack>

                    {shouldShowHomeOfficeDetails(values) && (
                        <Box sx={{ mt: 4 }}>
                            {/* Section Header with Why Important Link */}
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                pb={2}
                                sx={{
                                    borderBottom: 1,
                                    borderBottomColor: 'divider',
                                }}>
                                <Typography variant="h6" color="text.primary">
                                    Home Ownership
                                </Typography>
                                <WhyImportantLink
                                    context={ACCOUNTING_SETUP_HELP_CONTEXT.reimbursement}
                                    helpQuestion="Why does whether I own or rent my home change what I can deduct?"
                                />
                            </Stack>

                            {/* YesNoField for the question */}
                            <Box sx={{ mt: 3 }}>
                                <YesNoField
                                    name="rentOrOwn"
                                    label="Do you rent or own your home?"
                                    description="This affects which expenses you can claim for your home office."
                                    options={[
                                        { value: 'rent', label: 'Rent' },
                                        { value: 'own', label: 'Own' },
                                    ]}
                                    onChange={(value) => handleQuestionChange('rentOrOwn', value as string)}
                                />
                            </Box>
                        </Box>
                    )}

                    {shouldShowHomeOfficeDetails(values) && values.rentOrOwn && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, mt: 4 }}>
                                Home Office Details
                            </Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Office square footage
                                    </Typography>
                                    <Field name="officeSqft">
                                        {({
                                            field,
                                        }: {
                                            field: {
                                                name: string;
                                                value: string;
                                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                                                onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
                                            };
                                        }) => (
                                            <AmountField
                                                {...field}
                                                variant="outlined"
                                                size="small"
                                                placeholder="e.g., 200"
                                                fullWidth
                                                inputComponentProps={{
                                                    endAdornment: 'sqft',
                                                    allowNegative: false,
                                                }}
                                                inputMode="numeric"
                                                error={touched.officeSqft ? errors.officeSqft : undefined}
                                                isShowError={touched.officeSqft && !!errors.officeSqft}
                                                onBlur={(e) => {
                                                    field.onBlur(e as React.FocusEvent<HTMLInputElement>);
                                                }}
                                                onChange={(e) => {
                                                    const parsedValue = parseFloat((+e.target.value).toFixed(2));
                                                    field.onChange({
                                                        target: { name: 'officeSqft', value: parsedValue },
                                                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Total home square footage
                                    </Typography>
                                    <Field name="totalHomeSqft">
                                        {({
                                            field,
                                        }: {
                                            field: {
                                                name: string;
                                                value: string;
                                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
                                                onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
                                            };
                                        }) => (
                                            <AmountField
                                                {...field}
                                                variant="outlined"
                                                size="small"
                                                placeholder="e.g., 1500"
                                                fullWidth
                                                inputComponentProps={{
                                                    endAdornment: 'sqft',
                                                    allowNegative: false,
                                                }}
                                                inputMode="numeric"
                                                error={touched.totalHomeSqft ? errors.totalHomeSqft : undefined}
                                                isShowError={touched.totalHomeSqft && !!errors.totalHomeSqft}
                                                onBlur={(e) => {
                                                    field.onBlur(e as React.FocusEvent<HTMLInputElement>);
                                                }}
                                                onChange={(e) => {
                                                    const parsedValue = parseFloat((+e.target.value).toFixed(2));
                                                    field.onChange({
                                                        target: { name: 'totalHomeSqft', value: parsedValue },
                                                    } as unknown as React.ChangeEvent<HTMLInputElement>);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Box>

                {/* Confirmation message when no reimbursements are needed */}
                {hasNoReimbursements(values) && (
                    <Box sx={{ mt: 4 }}>
                        <Box
                            py={3}
                            px={4}
                            bgcolor="cement.50"
                            borderRadius={2}
                            mt={4}
                            sx={{ border: 1, borderColor: 'cement.100' }}>
                            <Typography variant="h5" mb={1}>
                                No reimbursements needed
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                {values.drivesForBusiness === 'no' && values.rentsOfficeSpace === 'yes'
                                    ? "Since you don't drive for business and rent office space, there's nothing to reimburse here. Continue to the next step."
                                    : "Since you have no home office or business mileage expenses, there's nothing to reimburse here. Continue to the next step."}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Big calculation box - only show when square footage inputs are visible */}
                {shouldShowHomeOfficeDetails(values) && values.rentOrOwn && (
                    <Box>
                        <Typography variant="subtitle1" mt={2}>
                            Percentage of reimbursable home office expenses
                        </Typography>
                        <Typography variant="body2">
                            We&apos;ll apply it to your home costs to figure out what your S Corp can reimburse.
                        </Typography>
                        <Box mt={2} borderRadius={3} border={1} p={2} sx={{ borderColor: 'grey.100' }}>
                            <Stack
                                py={2.5}
                                px={8}
                                borderRadius={3}
                                bgcolor="cement.50"
                                direction="row"
                                gap={2}
                                justifyContent="space-between"
                                alignItems="center">
                                <Stack sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="h2">{values.officeSqft || 0}</Typography>
                                    <Typography variant="body1" color="grey.600">
                                        Office sqft
                                    </Typography>
                                </Stack>
                                <Typography variant="h3">÷</Typography>
                                <Stack sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="h2">{values.totalHomeSqft || 0}</Typography>
                                    <Typography variant="body1" color="grey.600">
                                        Home sqft
                                    </Typography>
                                </Stack>
                                <Typography variant="h3">=</Typography>
                                <Stack sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="h2">{values.percentage}%</Typography>
                                    <Typography variant="body1" color="grey.600">
                                        Multiplier
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Form>
    );
}

const ReimbursementQuestionnaire = forwardRef<ReimbursementQuestionnaireRef, ReimbursementQuestionnaireProps>(
    ({ initialValues, homeAddress, onSubmit, setCanProceed = () => {} }, ref) => {
        const formikRef = React.useRef<FormikProps<UserAnswers> | null>(null);

        useImperativeHandle(ref, () => ({
            getCurrentValues: () => {
                return formikRef.current?.values || initialValues;
            },
        }));

        return (
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                enableReinitialize
                // Not per keystroke — canProceed is derived independently from
                // values (see effect above), not from Formik validity, so
                // per-change full-schema validation was pure waste (AP-1875).
                validateOnChange={false}
                validateOnBlur
                validateOnMount={false}>
                {(formikProps: FormikProps<UserAnswers>) => {
                    formikRef.current = formikProps;
                    const { values, errors, setFieldValue, touched } = formikProps;

                    return (
                        <QuestionnaireFormContent
                            values={values}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            homeAddress={homeAddress}
                            setCanProceed={setCanProceed}
                            touched={touched}
                        />
                    );
                }}
            </Formik>
        );
    }
);

ReimbursementQuestionnaire.displayName = 'ReimbursementQuestionnaire';
ReimbursementQuestionnaire.defaultProps = defaultProps;

export default ReimbursementQuestionnaire;
